import {
  ActionIcon,
  Button,
  Center,
  Divider,
  Group,
  Loader,
  Pagination,
  Stack,
  Table,
  Text,
  Title,
  Tooltip,
} from "@mantine/core";
import { DatePickerInput } from "@mantine/dates";
import { IconDownload } from "@tabler/icons-react";
import { safeApiCall } from "api/common.service";
import { productRecallSearch } from "api/product.service";
import { formatDate } from "common/utils";
import DebtorSelect from "pages/admin/components/DebtorSelection";
import ProductCodeInput from "pages/admin/components/ProductCodeInput";
import React, { useEffect, useState } from "react";
import { RptProductRecalls } from "types/model.types";

const AdminProductRecall: React.FC = () => {
  const [debtorId, setDebtorId] = useState<number | null>(null);
  const [data, setData] = useState<RptProductRecalls>([]);
  const [loading, setLoading] = useState(false);
  const apiFetchRef = React.useRef<boolean>(false);

  const [fromDate, setFromDate] = useState<Date | null>(null);
  const [toDate, setToDate] = useState<Date | null>(null);
  const [fromCode, setFromCode] = useState<number | undefined>();
  const [toCode, setToCode] = useState<number | undefined>();
  const [pageSize] = useState(30);
  const [pageNumber, setPageNumber] = useState(1);
  const [total, setTotal] = useState(0);

  /**
   *
   * @returns an array of customer order history data
   */
  const fetchData = async (download = false) => {
    if (apiFetchRef.current === true) return;
    apiFetchRef.current = true;

    setLoading(true);

    const result = await safeApiCall(
      productRecallSearch(
        pageSize,
        pageNumber,
        debtorId ?? 0,
        download,
        fromDate,
        toDate,
        fromCode,
        toCode,
      ),
    );
    setData(result?.data ?? []);
    apiFetchRef.current = false;
    setLoading(false);
    setTotal(result?.total ?? 0);
  };

  useEffect(() => {
    const today = new Date();
    const startOfYear = new Date(today.getFullYear(), 0, 1); // Jan 1st

    setFromDate(startOfYear);
    setToDate(today);
  }, []);

  useEffect(() => {
    fetchData();
  }, [pageNumber, pageSize]);

  return (
    <Stack>
      <Title order={2}>Product Recall Report</Title>
      <Group align="end" gap="sm">
        <DebtorSelect
          name="Debtor"
          ariaLabel="Debtor Selection"
          value={null}
          label="Select Account"
          placeholder="Type debtor name or id"
          onChange={(debtorId: number | null, _: string) => {
            setDebtorId(debtorId);
          }}
        />
        <DatePickerInput
          label="From Date"
          value={fromDate}
          dropdownType="modal"
          modalProps={{
            zIndex: 3000,
            size: "sm",
            withinPortal: true,
            trapFocus: false,
            centered: true,
            keepMounted: true,
          }}
          onChange={(value: string | null) =>
            setFromDate(value ? new Date(value) : null)
          }
          clearable
          w={250}
        />
        <DatePickerInput
          label="To Date"
          value={toDate}
          dropdownType="modal"
          modalProps={{
            zIndex: 3000,
            size: "sm",
            withinPortal: true,
            trapFocus: false,
            centered: true,
            keepMounted: true,
          }}
          onChange={(value: string | null) =>
            setToDate(value ? new Date(value) : null)
          }
          w={250}
          clearable
        />
      </Group>
      <Group align="end" gap="sm">
        <ProductCodeInput
          name="fromCode"
          label="From Product Code"
          value={fromCode}
          setValue={setFromCode}
          resetPage={() => setPageNumber(1)}
          maxLength={6} // e.g. max 6 digits
          min={0}
          max={999999}
        />
        <ProductCodeInput
          name="toCode"
          label="To Product Code"
          value={toCode}
          setValue={setToCode}
          resetPage={() => setPageNumber(1)}
          maxLength={6} // e.g. max 6 digits
          min={0}
          max={999999}
        />
        <Button
          size={"sm"}
          disabled={apiFetchRef.current}
          onClick={() => fetchData(false)}
        >
          Refresh
        </Button>
        <Button
          size={"sm"}
          disabled={apiFetchRef.current}
          onClick={() => {
            setFromCode(undefined);
            setToCode(undefined);
            setDebtorId(null);
          }}
        >
          Clear Filters
        </Button>
        <Tooltip label="Download CSV">
          <ActionIcon size={"36"} onClick={() => fetchData(true)}>
            <IconDownload size={24} />
          </ActionIcon>
        </Tooltip>
      </Group>
      <Divider label="Define filters above..."></Divider>
      <Group justify="flex-end" wrap="nowrap" mb="md">
        <Text size="sm" c="dimmed">
          Showing 30 items per page
        </Text>
        <Pagination
          total={Math.ceil(total / pageSize)}
          value={pageNumber}
          onChange={setPageNumber}
          siblings={1}
          boundaries={1}
        />
      </Group>
      <Table striped highlightOnHover withTableBorder withColumnBorders>
        <Table.Thead>
          <Table.Tr>
            <Table.Th>Debtor #</Table.Th>
            <Table.Th>Surname</Table.Th>
            <Table.Th>Product Code</Table.Th>
            <Table.Th>Description</Table.Th>
            <Table.Th>Invoice #</Table.Th>
            <Table.Th>Order #</Table.Th>
            <Table.Th>Order Date</Table.Th>
            <Table.Th>Created By</Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {loading ? (
            <Table.Tr>
              <Table.Td colSpan={8}>
                <Center>
                  <Loader />
                </Center>
              </Table.Td>
            </Table.Tr>
          ) : (
            <>
              {data.map((row, index) => (
                <Table.Tr key={`${index}-${row.id}`}>
                  <Table.Td>{row.id}</Table.Td>
                  <Table.Td>{row.customer}</Table.Td>
                  <Table.Td>
                    {row.productId}
                    {row.productCode}
                  </Table.Td>
                  <Table.Td>{row.productDesc}</Table.Td>
                  <Table.Td>{row.invoiceId}</Table.Td>
                  <Table.Td>{row.orderId}</Table.Td>
                  <Table.Td>{formatDate(row.createdOn)}</Table.Td>
                  <Table.Td>{row.createdBy}</Table.Td>
                </Table.Tr>
              ))}
            </>
          )}
        </Table.Tbody>
      </Table>
    </Stack>
  );
};

export default AdminProductRecall;
